import HTTP from './http-common';

/**
 * Handles version mismatch by performing appropriate refresh steps
 * @param {string} currentAppVersion - The current application version
 * @param {string} currentApiVersion - The current API version
 */
function handleVersionMismatch(currentAppVersion, currentApiVersion) {
  const isFirstRefresh = !localStorage.getItem('APP_VERSION_REFRESHED');

  if (isFirstRefresh) {
    return performFirstRefresh(currentAppVersion, currentApiVersion);
  } else {
    return performSecondRefresh(currentAppVersion, currentApiVersion);
  }
}

/**
 * Performs the first refresh step when a version mismatch is detected
 * @param {string} currentAppVersion - The current application version
 * @param {string} currentApiVersion - The current API version
 */
function performFirstRefresh(currentAppVersion, currentApiVersion) {
  localStorage.setItem('APP_VERSION_REFRESHED', 1);

  clearLocalStorage();

  HTTP.post('/store/clearCache');

  const versionUrl = buildVersionUrl(currentAppVersion, currentApiVersion);
  return setTimeout(() => (location.href = versionUrl), 1000);
}

/**
 * Performs the second refresh step after the first refresh
 * @param {string} currentAppVersion - The current application version
 * @param {string} currentApiVersion - The current API version
 */
function performSecondRefresh(currentAppVersion, currentApiVersion) {
  localStorage.removeItem('APP_VERSION_REFRESHED');

  if (currentAppVersion) localStorage.setItem('APP_VERSION', currentAppVersion);
  if (currentApiVersion) localStorage.setItem('API_VERSION', currentApiVersion);

  return location.reload(true);
}

/**
 * Builds a URL with version parameters
 * @param {string} currentAppVersion - The current application version
 * @param {string} currentApiVersion - The current API version
 * @returns {string} The URL with version parameters
 */
function buildVersionUrl(currentAppVersion, currentApiVersion) {
  let updatedUrl = location.href;

  const separator = updatedUrl.indexOf('?') > -1 ? '&' : '?';
  updatedUrl += separator;

  const versionParams = [
    { key: 'version', value: currentAppVersion.substr(0, 7) },
    { key: 'apiVersion', value: currentApiVersion.substr(0, 7) }
  ];

  versionParams.forEach(({ key, value }) => {
    if (!updatedUrl.match(key)) {
      updatedUrl += `${key}=${value}&`;
    } else {
      let existingValue = updatedUrl.split(`${key}=`)[1].split('&')[0];
      if (existingValue.toString() !== value.toString()) {
        updatedUrl = updatedUrl.replace(existingValue, value);
      }
    }
  });

  if (updatedUrl.endsWith('&')) updatedUrl = updatedUrl.substr(0, updatedUrl.length - 1);

  return updatedUrl;
}

/**
 * Clears local storage and session storage except for specified keys
 */
function clearLocalStorage() {
  const SKIP_KEYS = ['mpower-filters', 'access_token', 'APP_VERSION', 'API_VERSION', 'APP_VERSION_REFRESHED'];

  Object.keys(localStorage).forEach(function (key) {
    for (let i = 0; i < SKIP_KEYS.length; i++) {
      if (key.toLowerCase().match(SKIP_KEYS[i].toLowerCase())) {
        return;
      }
    }
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

/**
 * Checks if application versions match and handles refresh if needed
 * @returns {Promise<void>}
 */
export async function checkVersion() {
  const currentAppVersion = process.env.CODEBUILD_RESOLVED_SOURCE_VERSION || process.env.APP_VERSION;

  try {
    const isLocalEnvironment = process.env.NODE_ENV === 'local' || location.origin.match('localhost');
    if (isLocalEnvironment) return;

    const storedAppVersion = localStorage.getItem('APP_VERSION');
    const storedApiVersion = localStorage.getItem('API_VERSION');
    const currentApiVersion = (await HTTP.get('/stores/version')).data;

    const versionsExist = storedAppVersion && storedApiVersion;
    const appVersionMismatch = currentAppVersion && storedAppVersion !== currentAppVersion;
    const apiVersionMismatch = currentApiVersion !== storedApiVersion;
    const hasVersionMismatch = versionsExist && (appVersionMismatch || apiVersionMismatch);

    if (hasVersionMismatch) {
      return handleVersionMismatch(currentAppVersion, currentApiVersion);
    } else if (!storedAppVersion || !storedApiVersion) {
      if (currentAppVersion) localStorage.setItem('APP_VERSION', currentAppVersion);
      if (currentApiVersion) localStorage.setItem('API_VERSION', currentApiVersion);
    }

    localStorage.removeItem('APP_VERSION_REFRESHED');
  } catch (e) {
    console.log(e);
  }
}
